/**
 * Widget-level "Global Filter Bindings" editor. For every global filter defined on the
 * dashboard, renders a dropdown letting the user map the filter to a column of this
 * widget's datasets (primary and linked). Text filters additionally get a per-binding
 * comparison picker. Bindings are stored on the component's data query
 * (dataQuery0.globalFilterBindings) via setValue and are required for every
 * dataset-driven widget before the dashboard can be saved. Rows are greyed out when a
 * filter criterion of this widget references the global filter directly — the criterion
 * then overrides the binding.
 */
namespace distill.dashboards {
    const GLOBAL_FILTER_BINDING_MIN_RESULTS_FOR_SEARCH: string = '10';

    // The text searches the query layer builds as SQL LIKE: they read the column as text, so they apply only to a text column.
    const TEXT_SEARCH_OPERATORS: string[] = ['Contains', 'NotContains', 'StartsWith', 'NotStartsWith', 'EndsWith', 'NotEndsWith'];

    function referencesGlobalFilterInCriteria(filter: IDataQueryFilter | null | undefined, filterId: string): boolean {
        if (!filter)
            return false;
        if (filter.globalFilterId === filterId)
            return true;
        return (filter.groupConditions || []).some(g => referencesGlobalFilterInCriteria(g, filterId));
    }

    function referencesGlobalFilterInAggCriteria(filter: IDataQueryAggregationFilter | null | undefined, filterId: string): boolean {
        if (!filter)
            return false;
        if (filter.globalFilterId === filterId)
            return true;
        return (filter.groupConditions || []).some(g => referencesGlobalFilterInAggCriteria(g, filterId));
    }

    export class GlobalFilterBindingsEditor extends EditorBase {
        private $list!: JQuery;
        private _lastSignature: string | null = null;

        init(): void {
            this.$list = this.$editor.find('.global-filter-bindings-list');
        }

        update(): void {
            const component = this.component as IDashboardComponent;
            const filters = this.getGlobalFilters();
            const datasetRefs = getDatasetRefsInDataQuery(component.dataQuery0);

            if (datasetRefs.length === 0 || filters.length === 0) {
                this._lastSignature = null;
                this.$list.empty();
                this.setVisibility(false);
                return;
            }

            // Criteria references participate in the signature: a new reference grey-outs its row.
            const referencedIds = filters
                .filter(f => this.isFilterOverridden(component, f.id))
                .map(f => f.id)
                .join(',');

            // The binding values participate too: choosing a column reveals a Text filter's comparison
            // picker, and the chosen comparison sets its value — so the row must rebuild when either
            // changes (they are this editor's own value, not an unrelated design change).
            const bindings = this.getValueForEdit<IGlobalFilterBinding[]>() || [];
            const bindingTargets = bindings
                .map(b => `${b.globalFilterId}:${b.datasetIndex ?? ''}:${b.columnId ?? ''}:${b.metricIndex ?? ''}:${b.operator ?? ''}`)
                .join(',');
            const signature = `${datasetRefs.map(r => r.id).join('+')}|${filters.map(f => `${f.id}:${f.label}:${f.type}:${f.sourceDatasetId}:${f.valueColumn}`).join(',')}|${referencedIds}|${bindingTargets}`;

            // Avoid re-rendering (and re-initialising select2) on unrelated design changes.
            if (signature === this._lastSignature && this.$list.children().length > 0) {
                this.setVisibility(true);
                return;
            }

            this._lastSignature = signature;
            this.$list.empty();
            this.setVisibility(true);

            filters.forEach(filter => {
                const binding = bindings.find(b => b.globalFilterId === filter.id);
                this.$list.append(this.buildRow(component, filter, datasetRefs, binding));
            });

            appn.select.initForDesign({
                elements: this.$list.find('select').not('.select2-hidden-accessible'),
                omitWidth: true,
                minimumResultsForSearch: GLOBAL_FILTER_BINDING_MIN_RESULTS_FOR_SEARCH
            });
        }

        bindEvents(): void {
            // Global filters are edited in a separate dialog; re-render when the design changes.
            this.subscribeToEvent(EVENTS.STATE_CHANGED, () => this.update());

            this.$list.on('change', '.global-filter-binding-column', e => {
                const filterId = $(e.currentTarget).data('filter-id') as string;
                const val = ($(e.target).val() as string) || '';

                if (val.startsWith('metric|'))
                    this.setBinding(filterId, 0, null, parseInt(val.split('|')[1], 10));
                else if (val) {
                    const [datasetIndexStr, columnId] = val.split('|');
                    this.setBinding(filterId, parseInt(datasetIndexStr, 10), columnId);
                } else
                    this.setBinding(filterId, 0, null);
            });

            this.$list.on('change', '.global-filter-binding-operator', e => {
                const filterId = $(e.currentTarget).data('filter-id') as string;
                const val = ($(e.target).val() as string) || '';
                if (val)
                    this.setBindingOperator(filterId, val);
            });
        }

        private setBinding(filterId: string, datasetIndex: number, columnId: string | null, metricIndex?: number): void {
            const bindings = (this.getValueForEdit<IGlobalFilterBinding[]>() || []).slice();
            const existing = bindings.find(b => b.globalFilterId === filterId);
            const isMetric = metricIndex != null;
            const updated: IGlobalFilterBinding = {
                globalFilterId: filterId,
                datasetIndex: isMetric ? undefined : datasetIndex,
                columnId: !isMetric && columnId ? columnId : undefined,
                metricIndex: isMetric ? metricIndex : undefined,
                operator: isMetric ? undefined : existing?.operator
            };
            const index = bindings.findIndex(b => b.globalFilterId === filterId);

            if (index >= 0)
                bindings[index] = updated;
            else
                bindings.push(updated);

            this.setValue(bindings);
        }

        private setBindingOperator(filterId: string, operator: string): void {
            const bindings = (this.getValueForEdit<IGlobalFilterBinding[]>() || []).slice();
            const index = bindings.findIndex(b => b.globalFilterId === filterId);

            // The comparison lives on the binding — nothing to set without a mapped column.
            if (index < 0)
                return;

            bindings[index] = { ...bindings[index], operator };
            this.setValue(bindings);
        }

        private buildRow(component: IDashboardComponent, filter: IDashboardGlobalFilter, datasetRefs: IDatasetReference[], binding?: IGlobalFilterBinding): JQuery {
            const $row = appn.utils.cloneTemplate('globalFilterBindingRow');

            $row.find('.global-filter-binding-label').text(filter.label || '').attr('title', filter.label || '');

            const $columnCol = $row.find('.global-filter-binding-column-col');
            const $operatorCol = $row.find('.global-filter-binding-operator-col');
            const $select = $row.find('.global-filter-binding-column');
            const $warning = $row.find('.global-filter-binding-warning');

            // A criterion referencing the filter overrides this binding: the note takes the
            // column select's place — there is nothing editable to show.
            if (this.isFilterOverridden(component, filter.id)) {
                $select.addClass('d-none');
                $columnCol.find('.global-filter-binding-overridden').removeClass('d-none');
                return $row;
            }

            const selectedValue = binding?.metricIndex != null
                ? `metric|${binding.metricIndex}`
                : (binding?.columnId ? `${binding.datasetIndex ?? 0}|${binding.columnId}` : '');

            const availableTargets = this.populateBindingTargets($select, filter, datasetRefs, component.dataQuery0?.aggregation || null, selectedValue);

            if (availableTargets === 0) {
                $select.addClass('d-none');
                $warning.text(T(`No suitable column for '{0}' in this widget's datasets. Update the dataset or remove the global filter.`, filter.label || '')).removeClass('d-none');
                return $row;
            }

            $select.attr('data-filter-id', filter.id);

            // Text filters compare inline: the operator picker shares the row with the column select.
            if (filter.type === DashboardGlobalFilterType.Text && binding?.columnId) {
                $columnCol.removeClass('col-8').addClass('col-4 pr-1');
                $operatorCol.removeClass('d-none');
                const $operatorSelect = $operatorCol.find('.global-filter-binding-operator');
                $operatorSelect.attr('data-filter-id', filter.id);

                // A text search needs a text column (the query layer builds `col LIKE @p`); the equality pair fits any column.
                const { columnMeta } = getContextualColumnMeta(binding.datasetIndex, binding.columnId, datasetRefs, designer.getDatasets() || []);
                const isTextColumn = columnMeta?.type === 'Text' || columnMeta?.type === 'LongText';
                // Values are the FilterOperator names the binding stores (matching the criterion editor); labels are their display names.
                const operators: { value: string; label: string }[] = [
                    { value: 'Equals', label: T('equals') },
                    { value: 'NotEquals', label: T('not equals') },
                    { value: 'Contains', label: T('contains') },
                    { value: 'NotContains', label: T('does not contain') },
                    { value: 'StartsWith', label: T('starts with') },
                    { value: 'NotStartsWith', label: T('does not start with') },
                    { value: 'EndsWith', label: T('ends with') },
                    { value: 'NotEndsWith', label: T('does not end with') }
                ];

                operators
                    .filter(op => isTextColumn || !TEXT_SEARCH_OPERATORS.includes(op.value))
                    .forEach(op => $operatorSelect.append($('<option>').val(op.value).text(op.label)));

                // A text column defaults to a substring search; any other column to an exact match.
                $operatorSelect.val(binding.operator || (isTextColumn ? 'Contains' : 'Equals'));
            }

            return $row;
        }

        private populateBindingTargets($select: JQuery, filter: IDashboardGlobalFilter, datasetRefs: IDatasetReference[], aggregation: IDataQueryAggregation | null, selectedValue: string): number {
            const allDatasets = designer.getDatasets() || [];
            const compatibleTypes = getGlobalFilterCompatibleColumnTypes(filter, allDatasets);
            const showPrefix = datasetRefs.length > 1;

            $select.empty().append($('<option>').val('').text(T('Choose column...')));

            const buildTargetText = (datasetRef: IDatasetReference, dataset: IDatasetMeta, columnMeta: IDatasetColumnMeta): string => {
                const columnText = getColumnOptionText(columnMeta);
                return showPrefix ? `${datasetRef.alias || dataset.name}.${columnText}` : columnText;
            };

            // Group-by / break-down-by columns surface in their own optgroup rather than the
            // plain column list.
            const groupTargetValue = aggregation?.groupBy?.columnId
                ? `${aggregation.groupBy.datasetIndex ?? 0}|${aggregation.groupBy.columnId}` : null;
            const breakDownTargetValue = aggregation?.breakDownBy?.columnId
                ? `${aggregation.breakDownBy.datasetIndex ?? 0}|${aggregation.breakDownBy.columnId}` : null;

            let count = 0;
            const $columnsGroup = $('<optgroup>').attr('label', T('Columns'));
            datasetRefs.forEach((datasetRef, index) => {
                const dataset = allDatasets.find(t => t.id === datasetRef.id);
                if (!dataset)
                    return;

                dataset.columns.forEach(col => {
                    if (!compatibleTypes.includes(col.type))
                        return;

                    const value = `${index}|${col.id}`;
                    if (value === groupTargetValue || value === breakDownTargetValue)
                        return;

                    $columnsGroup.append($('<option>').val(value).text(buildTargetText(datasetRef, dataset, col)));
                    count++;
                });
            });
            if (count > 0)
                $select.append($columnsGroup);

            if (aggregation) {
                const groupOptions: { value: string; text: string }[] = [];
                const addGroupTarget = (identifier: IDataQueryColumnIdentifier | undefined): void => {
                    if (!identifier?.columnId)
                        return;

                    const { datasetRef, dataset, columnMeta } = getContextualColumnMeta(identifier.datasetIndex, identifier.columnId, datasetRefs, allDatasets);
                    if (!datasetRef || !dataset || !columnMeta || !compatibleTypes.includes(columnMeta.type))
                        return;

                    groupOptions.push({ value: `${identifier.datasetIndex ?? 0}|${identifier.columnId}`, text: buildTargetText(datasetRef, dataset, columnMeta) });
                };
                addGroupTarget(aggregation.groupBy);
                addGroupTarget(aggregation.breakDownBy);

                if (groupOptions.length > 0) {
                    const $groups = $('<optgroup>').attr('label', T('Groups'));
                    groupOptions.forEach(o => $groups.append($('<option>').val(o.value).text(o.text)));
                    $select.append($groups);
                    count += groupOptions.length;
                }

                // Metrics are aggregated numbers — only number filters can target them.
                if (filter.type === DashboardGlobalFilterType.NumberRange && aggregation.metrics?.length > 0) {
                    const $metricsGroup = $('<optgroup>').attr('label', T('Metrics'));
                    aggregation.metrics.forEach((m, index) => {
                        $metricsGroup.append($('<option>').val(`metric|${index}`).text(getMetricDisplayText(m, index, aggregation, datasetRefs, allDatasets)));
                    });
                    $select.append($metricsGroup);
                    count += aggregation.metrics.length;
                }
            }

            if (selectedValue)
                $select.val(selectedValue);

            return count;
        }

        private isFilterOverridden(component: IDashboardComponent, filterId: string): boolean {
            const query = component.dataQuery0;
            if (!query)
                return false;
            return referencesGlobalFilterInCriteria(query.filter, filterId)
                || referencesGlobalFilterInAggCriteria(query.aggregationFilter, filterId);
        }

        private getGlobalFilters(): IDashboardGlobalFilter[] {
            const design = designer._design as IDashboardDesign;
            return (design && design.globalFilters) ? design.globalFilters : [];
        }
    }

    editors.register('globalFilterBindings', GlobalFilterBindingsEditor);
}
